// Wires up the "Fehler melden" form: records when the form was rendered
// (for the backend's timing-based spam check), pre-fills the page number
// from the selected song, and submits via fetch instead of a full page
// reload. No-ops entirely on pages that don't have this form.

// In production this is served from the same origin as the site itself
// (the reverse proxy routes this path to the intake service), so a plain
// relative path is used and there is no CORS involved at all. The
// Bridgetown dev server (docker-compose port 4022) has no such proxy, so
// when running locally we talk to the intake service directly on its own
// port instead; see errata-intake's README for the matching
// CORS_ALLOWED_ORIGIN dev setting.
const SUBMIT_ENDPOINT =
  window.location.port === "4022"
    ? "http://localhost:8080/api/errata/submit"
    : "/api/errata/submit"

// Native <input list> + <datalist> turned out to be too unreliable across
// browsers for this (Firefox in particular: the popup often only appears
// on the second interaction, and can fill the input with the option's
// visible text instead of its `value`, which broke submitting the actual
// song id). This is a small hand-rolled combobox instead, so both the
// open/close behavior and what ends up in the hidden song_id field are
// fully under our control.
// Songs are labelled by their opening line (starttext) with the printed
// title in parentheses, e.g. "Zupf dir ein Wölkchen aus dem Wolkenweiß
// (Wolkenzupf)" — that's how people actually recall a song. Some entries
// have no recorded starttext, in which case the title alone is used.
function songLabel(song) {
  return song.starttext ? `${song.starttext} (${song.title})` : song.title
}

function initSongCombobox(songs) {
  const songInput = document.getElementById("errata-song")
  const songIdInput = document.getElementById("errata-song-id")
  const listbox = document.getElementById("errata-song-listbox")
  const pageInput = document.getElementById("errata-page")

  let filtered = songs
  let activeIndex = -1

  function renderList() {
    listbox.innerHTML = ""
    filtered.forEach((song, index) => {
      const li = document.createElement("li")
      li.textContent = songLabel(song)
      li.setAttribute("role", "option")
      if (index === activeIndex) {
        li.classList.add("errata-listbox__option--active")
      }
      // mousedown (not click) fires before the input's blur handler closes
      // the list, so the click doesn't get swallowed by the close-on-blur.
      li.addEventListener("mousedown", (event) => {
        event.preventDefault()
        selectSong(song)
      })
      listbox.appendChild(li)
    })
    listbox.hidden = filtered.length === 0
  }

  function filterSongs(query) {
    const q = query.trim().toLowerCase()
    filtered = q ? songs.filter((song) => songLabel(song).toLowerCase().includes(q)) : songs
    activeIndex = -1
    renderList()
  }

  function selectSong(song) {
    songInput.value = songLabel(song)
    songIdInput.value = song.id
    pageInput.value = song.page
    listbox.hidden = true
  }

  // If the visible text no longer matches the previously selected song
  // (the user edited it, or typed something that was never selected), the
  // hidden song_id must not keep pointing at a stale/mismatched song.
  function invalidateSelectionIfStale() {
    const selected = songs.find((song) => song.id === songIdInput.value)
    if (!selected || songLabel(selected) !== songInput.value) {
      songIdInput.value = ""
    }
  }

  songInput.addEventListener("focus", () => filterSongs(songInput.value))
  songInput.addEventListener("input", () => {
    invalidateSelectionIfStale()
    filterSongs(songInput.value)
  })
  songInput.addEventListener("blur", () => {
    setTimeout(() => {
      listbox.hidden = true
    }, 0)
  })
  songInput.addEventListener("keydown", (event) => {
    if (listbox.hidden && event.key !== "ArrowDown") return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (listbox.hidden) filterSongs(songInput.value)
      activeIndex = Math.min(activeIndex + 1, filtered.length - 1)
      renderList()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      activeIndex = Math.max(activeIndex - 1, 0)
      renderList()
    } else if (event.key === "Enter" && activeIndex >= 0 && filtered[activeIndex]) {
      event.preventDefault()
      selectSong(filtered[activeIndex])
    } else if (event.key === "Escape") {
      listbox.hidden = true
    }
  })
}

function initErrataForm() {
  const form = document.getElementById("errata-form")
  if (!form) return

  const renderedAt = Date.now()

  const songsDataEl = document.getElementById("errata-songs-data")
  const songs = songsDataEl ? JSON.parse(songsDataEl.textContent) : []
  initSongCombobox(songs)

  const songIdInput = document.getElementById("errata-song-id")
  const songInput = document.getElementById("errata-song")
  const statusEl = document.getElementById("errata-status")

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    statusEl.textContent = ""
    statusEl.classList.remove("errata-status--error")

    if (!songIdInput.value) {
      statusEl.textContent = "Bitte ein Lied aus der Liste auswählen."
      statusEl.classList.add("errata-status--error")
      songInput.focus()
      return
    }

    const formData = new FormData(form)
    const payload = {
      song_id: formData.get("song_id") || "",
      page: formData.get("page") || "",
      description: formData.get("description") || "",
      contact: formData.get("contact") || "",
      hp: formData.get("hp") || "",
      rendered_at: renderedAt,
    }

    const submitButton = form.querySelector("button[type=submit]")
    submitButton.disabled = true

    try {
      const response = await fetch(SUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        form.reset()
        statusEl.textContent =
          "Danke! Deine Meldung wurde übermittelt und wird von uns geprüft."
      } else {
        const body = await response.json().catch(() => ({}))
        statusEl.textContent =
          body.error ||
          "Die Meldung konnte nicht übermittelt werden. Bitte versuch es später erneut."
        statusEl.classList.add("errata-status--error")
      }
    } catch (err) {
      statusEl.textContent =
        "Die Meldung konnte nicht übermittelt werden. Bitte versuch es später erneut."
      statusEl.classList.add("errata-status--error")
    } finally {
      submitButton.disabled = false
    }
  })
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initErrataForm)
} else {
  initErrataForm()
}
